import React from "react";
import {
	Grid2 as Grid,
	Typography,
	MenuItem,
	Select,
	TextField,
	Collapse,
	Table,
	TableBody,
	TableCell,
	TableRow,
	styled,
	TableContainer,
	Button,
	List,
	ListItem,
	Box,
	FormControl,
	InputLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import CreditRequestStatusLabel from "../StatusLabel/CreditRequestStatusLabel";
import PencilIcon from "@jbhi-fi/lanyard-ui/components/Icons/Pencil";
import { creditStatus } from "../../util/creditRequestMapping";
import PageContext from "../../components/Common/PageContext";
import { getTimestampInLocalTime } from "@/util/dateUtil";
import { outlinedSecondaryActionStyles } from "../Common/modalActionStyles";

const useStyles = makeStyles((theme) => ({
	paddingRight: {
		paddingRight: 10,
	},
	paddingBottom: {
		paddingBottom: 10,
	},
	minorPaddingBottom: {
		paddingBottom: 8,
	},
	fullWidth: {
		width: "95%",
	},
	description: {
		display: "inline-block",
		paddingTop: 5,
		paddingBottom: 5,
	},
	credited: {
		paddingTop: 5,
		color: theme.palette.custom.creditRequest.pendingReview,
	},
	list: {
		padding: "0",
		width: "100%",
	},
	textField: {
		width: "100%",
		maxWidth: 350,
	},
	select: {
		width: "100%",
		minWidth: 200,
		maxWidth: 350,
		fontSize: "15px",
	},
	editButton: {
		color: theme.palette.text.primary,
	},
	defaultBorder: {
		border: 0,
		borderStyle: "solid",
		borderLeftWidth: 4,
		borderColor: theme.palette.custom.creditRequest.default,
	},
	inProgressBorder: {
		border: 0,
		borderStyle: "solid",
		borderLeftWidth: 4,
		borderColor: theme.palette.custom.creditRequest.inProgress,
	},
	draftBorder: {
		border: 0,
		borderStyle: "solid",
		borderLeftWidth: 4,
		borderColor: theme.palette.custom.creditRequest.draft,
	},
	awaitingSupplierBorder: {
		border: 0,
		borderStyle: "solid",
		borderLeftWidth: 4,
		borderColor: theme.palette.custom.creditRequest.awaitingSupplier,
	},
	pendingReviewBorder: {
		border: 0,
		borderStyle: "solid",
		borderLeftWidth: 4,
		borderColor: theme.palette.custom.creditRequest.pendingReview,
	},
	reviewingBorder: {
		border: 0,
		borderStyle: "solid",
		borderLeftWidth: 4,
		borderColor: theme.palette.custom.creditRequest.reviewing,
	},
}));

const StyledTableCell = styled(TableCell)({
	padding: 0,
});

const focusedOutlinedInputSx = {
	backgroundColor: "custom.creditRequest.fieldBackground",
	"&&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: "text.primary",
	},
};

export default function CreditRequestLineItem(props) {
	const classes = useStyles();
	const [open] = React.useState(false);
	const [editable, setEditable] = React.useState(false);
	const [saveError, setSaveError] = React.useState(false);
	const { updateLineItem, saveChanges, cancelChanges, lineItem } = props;
	const [pageContext] = React.useContext(PageContext);
	const { carriers: couriers } = pageContext;

	const handleSupplierReasonChange = (event) => {
		updateLineItem({ ...lineItem, supplierReason: event.target.value });
	};
	const handleCourierChange = (event) => {
		updateLineItem({ ...lineItem, receivedCarrierName: event.target.value });
	};
	const handleReturnCourierChange = (event) => {
		updateLineItem({ ...lineItem, returnCarrierName: event.target.value });
	};
	const onConsignmentNumberChange = (event) => {
		updateLineItem({ ...lineItem, receivedConsignmentNumber: event.target.value });
	};
	const onRANumberChange = (event) => {
		updateLineItem({ ...lineItem, reference: event.target.value });
	};
	const onReturnConsignmentNumberChange = (event) => {
		updateLineItem({ ...lineItem, returnConsignmentNumber: event.target.value });
	};
	const onSaveChanges = () => {
		if (checkSaveError()) {
			return;
		}
		saveChanges();
		setEditable(!editable);
	};
	const onCancelChanges = () => {
		cancelChanges();
		setEditable(!editable);
	};
	const checkSaveError = () => {
		if (!lineItem.receivedConsignmentNumber) {
			setSaveError(true);
			return true;
		}
		setSaveError(false);
		return false;
	};

	const checkShowEditButton = () => {
		return !editable && lineItem.status !== creditStatus.cancelled && lineItem.status !== creditStatus.claimed;
	};
	const getBorderColour = () => {
		if (!editable) {
			return classes.defaultBorder;
		}
		switch (lineItem.status) {
			default:
			case creditStatus.inProgress:
				return classes.inProgressBorder;
			case creditStatus.draft:
				return classes.draftBorder;
			case creditStatus.awaitingSupplier:
				return classes.awaitingSupplierBorder;
			case creditStatus.pendingReview:
				return classes.pendingReviewBorder;
			case creditStatus.reviewing:
				return classes.reviewingBorder;
		}
	};

	return (
		<List className={classes.list}>
			<ListItem className={classes.list}>
				<TableContainer>
					<Table>
						<TableBody>
							<TableRow className={`${getBorderColour()}`}>
								<StyledTableCell>
									<Grid
										container
										direction="row"
										alignItems={"flex-start"}
										className={classes.fullWidth}
										paddingLeft={2}
									>
										<Grid container size={12}>
											<Typography display="inline" className={classes.description}>
													<b>{lineItem.description}</b>
											</Typography>
										</Grid>
										<Grid container size={6}>
											<Grid size="grow">
												<Box>
													<Typography variant="caption" display="inline">
														<strong>SKU:</strong> {lineItem.sku}
													</Typography>
												</Box>
												<Box>
													<Typography variant="caption" display="inline">
														<strong>MODEL:</strong> {lineItem.modelNumber}
													</Typography>
												</Box>
												<Box>
													<Typography variant="caption" display="inline">
														<strong>Date Created:</strong> {getTimestampInLocalTime(lineItem.dateCreated, "dd/MM/yyyy")}
													</Typography>
												</Box>
											</Grid>
										</Grid>
										<Grid container size={6} className={classes.paddingBottom} sx={{ textAlign: "right" }}>
											<Grid size="grow">
												<Box>
													<Typography variant="caption" display="inline">
														<strong>Credited: {lineItem.quantity} </strong>
													</Typography>
												</Box>
												<Box>
													<Typography variant="caption" display="inline">
														Unit Ex: {"$" + (lineItem.unitCostEx ?? 0).toFixed(2)}
													</Typography>
												</Box>
												<Box>
													<Typography variant="caption" display="inline">
														Line Inc: {"$" + (lineItem.lineCostInc ?? 0).toFixed(2)}
													</Typography>
												</Box>
											</Grid>
										</Grid>
										<Grid container size={12} paddingTop={2} paddingBottom={editable ? 3 : 2} justifyContent={"space-between"}>
											<Box>
												<CreditRequestStatusLabel status={lineItem.status} />
											</Box>
											<Box>
												{checkShowEditButton() && (
													<Button
														aria-label="edit row"
														size="small"
														startIcon={<PencilIcon />}
														onClick={() => setEditable(!editable)}
														className={classes.editButton}
													>
														Edit
													</Button>
												)}
											</Box>
										</Grid>
										<Grid container size={12}>
											{editable ? (
												<Box className={classes.minorPaddingBottom} width="100%">
													<TextField
														error={saveError && !lineItem.receivedConsignmentNumber}
														label={"Consignment Number"}
														id="consignmentnumber"
														onChange={onConsignmentNumberChange}
														value={lineItem.receivedConsignmentNumber}
														variant="outlined"
														className={`${classes.textField}`}
														slotProps={{ input: { sx: focusedOutlinedInputSx } }}
														autoComplete="off"
														fullWidth
													/>
												</Box>
											) : (
												<Typography variant="caption" className={classes.minorPaddingBottom}>
													<strong>Consignment Number</strong>: {lineItem.receivedConsignmentNumber}
												</Typography>
											)}
										</Grid>
										<Grid container size={12}>
											{editable ? (
												<Box className={classes.minorPaddingBottom} width="100%">
													<FormControl fullWidth className={`${classes.select}`} variant="outlined">
															<InputLabel id="courier-label">Courier</InputLabel>
															<Select
																labelId="courier-label"
																value={lineItem.receivedCarrierName ? lineItem.receivedCarrierName : ""}
																onChange={handleCourierChange}
																label="Courier"
																sx={focusedOutlinedInputSx}
															>
															<MenuItem value=""><em>None</em></MenuItem>
															{couriers.map((courier) => (
																<MenuItem key={courier} value={courier}>
																	{courier}
																</MenuItem>
															))}
															</Select>
													</FormControl>
												</Box>
											) : (
												<Typography variant="caption" className={classes.minorPaddingBottom}>
													<strong>Courier</strong>: {lineItem.receivedCarrierName}
												</Typography>
											)}
										</Grid>
										<Grid container size={12}>
											{editable ? (
												<Box className={classes.minorPaddingBottom} width="100%">
													<FormControl fullWidth className={`${classes.select}`} variant="outlined">
														<InputLabel id="supplier-reason-label">Supplier Reason</InputLabel>
														<Select
															labelId="supplier-reason-label"
															value={lineItem.supplierReason ? lineItem.supplierReason : ""}
															onChange={handleSupplierReasonChange}
															label="Supplier Reason"
															sx={focusedOutlinedInputSx}
														>
														<MenuItem value=""><em>None</em></MenuItem>
														{props.reasons.map(
															(reason) =>
																reason.type === "Supplier" && (
																	<MenuItem key={reason.value} value={reason.value}>
																		{reason.value}
																	</MenuItem>
																)
														)}
														</Select>
													</FormControl>
												</Box>
											) : (
												<Typography variant="caption" className={classes.minorPaddingBottom}>
													<strong>Supplier's reason</strong>: {lineItem.supplierReason}
												</Typography>
											)}
										</Grid>
										<Grid container size={12}>
											{editable ? (
												<Box className={classes.minorPaddingBottom} width="100%">
													<TextField
														id="ranumber"
														label={"Supplier RA No"}
														onChange={onRANumberChange}
														value={lineItem.reference}
														className={`${classes.textField}`}
														slotProps={{ input: { sx: focusedOutlinedInputSx } }}
														autoComplete="off"
														variant="outlined"
														fullWidth
													/>
												</Box>
											) : (
												<Typography variant="caption" className={classes.minorPaddingBottom}>
													<strong>Supplier RA No</strong>: {lineItem.reference}
												</Typography>
											)}
										</Grid>
										<Grid container size={12}>
											{editable ? (
												<Box className={classes.minorPaddingBottom} width="100%">
													<TextField
														id="returnconsignmentnumber"
														label={"Return Consignment No"}
														onChange={onReturnConsignmentNumberChange}
														value={lineItem.returnConsignmentNumber}
														className={`${classes.textField}`}
														slotProps={{ input: { sx: focusedOutlinedInputSx } }}
														autoComplete="off"
														variant="outlined"
														fullWidth
													/>
												</Box>
											) : (
												<Typography variant="caption" className={classes.minorPaddingBottom}>
													<strong>Return Consignment No</strong>: {lineItem.returnConsignmentNumber}
												</Typography>
											)}
										</Grid>										
										<Grid container size={12}>
											{editable ? (
												<Box className={classes.minorPaddingBottom} width="100%">
													<FormControl fullWidth className={`${classes.select}`} variant="outlined">
														<InputLabel id="return-courier-label">Return Courier</InputLabel>
														<Select
															labelId="return-courier-label"
															value={lineItem.returnCarrierName ? lineItem.returnCarrierName : ""}
															onChange={handleReturnCourierChange}
															label="Return Courier"
															sx={focusedOutlinedInputSx}
														>
														<MenuItem value=""><em>None</em></MenuItem>
														{couriers.map((courier) => (
															<MenuItem key={courier} value={courier}>
																{courier}
															</MenuItem>
														))}
														</Select>
													</FormControl>
												</Box>
											) : (
												<Typography variant="caption" className={classes.minorPaddingBottom}>
													<strong>Return Courier</strong>: {lineItem.returnCarrierName}
												</Typography>
											)}
										</Grid>
									</Grid>
								</StyledTableCell>
							</TableRow>
							<TableRow>
								<StyledTableCell colSpan={5}>
									<Collapse in={open} timeout="auto" unmountOnExit className={classes.fullWidth}></Collapse>
								</StyledTableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</ListItem>
			<ListItem className={classes.list} sx={{ justifyContent: "center"}}>
				{editable && (
					<Box display="flex" flexDirection="column" gap={1} className={classes.fullWidth} maxWidth={500} paddingTop={2}>
							<Button
								variant="outlined"
								fullWidth
								onClick={onSaveChanges}
								className={classes.paddingRight}
								sx={outlinedSecondaryActionStyles}
							>
								Save Changes
							</Button>

							<Button
								variant="text"
								fullWidth
								onClick={onCancelChanges}
								className={classes.paddingRight}
								sx={{
									textTransform: "none",
									fontWeight: "normal",
									textDecoration: "underline",
									color: "text.primary",
									"&:hover": { textDecoration: "underline" },
								}}
							>
								Cancel
							</Button>
					</Box>
				)}
			</ListItem>
		</List>
	);
}
CreditRequestLineItem.propTypes = {
	//Todo: set lineitem required fields
	lineItem: PropTypes.object.isRequired,
	updateLineItem: PropTypes.func.isRequired,
	saveChanges: PropTypes.func.isRequired,
	cancelChanges: PropTypes.func.isRequired,
	reasons: PropTypes.array.isRequired,
	reason: PropTypes.string,
};
